/** Matches ATTRIBUTE_PREFIX in app/api's acumatica/entities.ts. */
export const ATTRIBUTE_PREFIX = 'attr.';

export const isAttribute = (column: string) => column.startsWith(ATTRIBUTE_PREFIX);
export const attributeLabel = (column: string) => column.slice(ATTRIBUTE_PREFIX.length);

/** Matches NESTED_SEPARATOR in app/api's acumatica/entities.ts. */
export const isNested = (column: string) => column.includes('/');

/*
 * Nested columns arrive as their Acumatica $expand path —
 * 'MainContact/Address/PostalCode'. That is the right name on the wire, in the
 * undo log and in an error message, because it is the field's actual address
 * and stays true when someone has to go look at it in Acumatica. It is the
 * wrong name for a column heading.
 *
 * Only nested columns are relabelled. Flat ones keep the name Acumatica gives
 * them, which is what a customer sees on their own screens and in their own
 * import templates — renaming CustomerClass to "Customer class" would look
 * tidier and match nothing they know.
 */
const NESTED_LABELS: Record<string, string> = {
  'MainContact/Email': 'Email',
  'MainContact/Phone1': 'Phone',
  'MainContact/Phone2': 'Phone 2',
  'MainContact/Fax': 'Fax',
  'MainContact/WebSite': 'Website',
  'MainContact/Attention': 'Attention',
  'MainContact/Address/AddressLine1': 'Address 1',
  'MainContact/Address/AddressLine2': 'Address 2',
  'MainContact/Address/City': 'City',
  'MainContact/Address/State': 'State',
  'MainContact/Address/PostalCode': 'Postal code',
  'MainContact/Address/Country': 'Country',

  /*
   * Not a nested field, but it needs a label for the same reason they do:
   * Acumatica spells it lowercase, and "note written" in an undo log reads
   * like a sentence that lost its capital rather than a field name.
   *
   * It lives here rather than being special-cased at one call site, which is
   * how the merge screen and the undo screen came to disagree.
   */
  note: 'Note',
};

/** What to show in a column heading or a diff row. */
export function fieldLabel(column: string): string {
  if (isAttribute(column)) return attributeLabel(column);
  // Unmapped nested field: the leaf is still better than the whole path, and
  // this stays correct when a field is added to the API before this map.
  return NESTED_LABELS[column] ?? column.split('/').pop() ?? column;
}

/** The full path, for a tooltip — so an ambiguous label can still be resolved. */
export function fieldTitle(column: string): string | undefined {
  return isNested(column) ? column : undefined;
}
